import React from 'react';
import { createPortal } from 'react-dom';

const fmt = (n) =>
  n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

// 'YYYY-MM-DD' (or Date) -> 'DD-MM-YYYY', matching the physical slip's date format.
function formatSlipDate(d) {
  const iso = d instanceof Date ? d.toISOString().slice(0, 10) : String(d).slice(0, 10);
  const [y, m, day] = iso.split('-');
  return `${day}-${m}-${y}`;
}

// BUY prints as "PEMBAYARAN", SELL prints as "PEMBELIAN" — per business terminology,
// intentionally not the literal Indonesian translation of buy/sell.
const TUJUAN_BY_TYPE = { BUY: 'PEMBAYARAN', SELL: 'PEMBELIAN' };

/**
 * Print-only transaction slip: 21cm wide (A4 width), portrait, capped at half
 * of A4's height (~14.85cm) — a standard A4 sheet with the slip occupying only
 * the top half. Rendered via a portal into #print-portal (a sibling of #root
 * in public/index.html), hidden on screen and shown only when printing — see
 * index.css for why it must live outside the main app tree.
 */
export default function PrintSlip({ row, customer, currency, store }) {
  if (!row) return null;

  const portalTarget = document.getElementById('print-portal');
  if (!portalTarget) return null;

  return createPortal(
    <div className="mx-auto max-h-[148mm] w-[194mm] px-3 py-2 text-black overflow-hidden">
      <div className="flex items-start justify-between border-b border-dotted border-black pb-1">
        <div>
          <div className="text-base font-bold">{store?.StoreName || ''}</div>
          <div className="text-xs">Telp : {store?.Phone || ''}</div>
        </div>
        <div className="text-base font-bold">SLIP TRANSAKSI / TRANSACTION SLIP</div>
      </div>

      <div className="mt-1 flex justify-between text-xs">
        <table>
          <tbody>
            <tr>
              <td className="pr-2 align-top">Nama / Name</td>
              <td className="pr-1 align-top">:</td>
              <td className="align-top">{customer?.customer_name || ''}</td>
            </tr>
            <tr>
              <td className="pr-2 align-top">Alamat / Address</td>
              <td className="pr-1 align-top">:</td>
              <td className="align-top">{customer?.address || ''}</td>
            </tr>
            <tr>
              <td className="pr-2 align-top">No. KTP</td>
              <td className="pr-1 align-top">:</td>
              <td className="align-top">{customer?.ktp || ''}</td>
            </tr>
            <tr>
              <td className="pr-2 align-top">Telpon / Phone</td>
              <td className="pr-1 align-top">:</td>
              <td className="align-top"></td>
            </tr>
          </tbody>
        </table>
        <table>
          <tbody>
            <tr>
              <td className="pr-2 align-top">No. #</td>
              <td className="pr-1 align-top">:</td>
              <td className="align-top">{row.doc_no}</td>
            </tr>
            <tr>
              <td className="pr-2 align-top">Date</td>
              <td className="pr-1 align-top">:</td>
              <td className="align-top">{formatSlipDate(row.transaction_date)}</td>
            </tr>
            <tr>
              <td className="pr-2 align-top">Tujuan</td>
              <td className="pr-1 align-top">:</td>
              <td className="align-top">{TUJUAN_BY_TYPE[row.transaction_type] || ''}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <table className="mt-1 w-full border-collapse text-xs">
        <thead>
          <tr>
            <th className="border border-black px-1 py-0.5">No</th>
            <th className="border border-black px-1 py-0.5">Kode</th>
            <th className="border border-black px-1 py-0.5">Mata Uang / Currency</th>
            <th className="border border-black px-1 py-0.5">Jual / Sell</th>
            <th className="border border-black px-1 py-0.5">Jumlah / Amount</th>
            <th className="border border-black px-1 py-0.5">Kurs / Rate</th>
            <th className="border border-black px-1 py-0.5">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="h-10 border border-black px-1 pt-0.5 align-top">1</td>
            <td className="h-10 border border-black px-1 pt-0.5 align-top">{currency?.currency_code || ''}</td>
            <td className="h-10 border border-black px-1 pt-0.5 align-top">{currency?.currency_name || ''}</td>
            <td className="h-10 border border-black px-1 pt-0.5 align-top">BANK NOTE</td>
            <td className="h-10 border border-black px-1 pt-0.5 text-right align-top">{fmt(row.valas)}</td>
            <td className="h-10 border border-black px-1 pt-0.5 text-right align-top">{fmt(row.kurs)}</td>
            <td className="h-10 border border-black px-1 pt-0.5 text-right align-top">{fmt(row.amount)}</td>
          </tr>
        </tbody>
      </table>

      <div className="mt-1 flex items-start text-xs">
        <div className="w-1/4" />
        <div className="flex-1 text-center">
          <div>☐ BIZ4x passed &nbsp;&nbsp;&nbsp; ☐ Treshold usd 10k passed</div>
          <div>☐ Treshold usd 10k not passed</div>
        </div>
        <div className="w-1/4 text-right font-semibold">Grand Total : {fmt(row.amount)}</div>
      </div>

      <div className="mt-1 text-xs">Payment : {row.bank_name}</div>

      <div className="mt-6 flex justify-between text-xs">
        <div className="w-1/3 text-center">
          <div>Dicetak / Printed by</div>
          <div className="mx-auto mt-6 w-32 border-t border-black" />
        </div>
        <div className="w-1/3 text-center">
          <div>Diperiksa / Checked By</div>
          <div className="mx-auto mt-6 w-32 border-t border-black" />
        </div>
        <div className="w-1/3 text-center">
          <div>Diterima / Received By</div>
          <div className="mx-auto mt-6 w-32 border-t border-black" />
        </div>
      </div>
    </div>,
    portalTarget
  );
}
